/**************************************************************************************************************************
 Copyright (c) 2024 WorkEarly Results LLP

     Name: basic.components.tsx
   Author: Harikrishnan Gangadharan
 Comments: 

/**************************************************************************************************************************
 IMPORTS
***************************************************************************************************************************/
import { ImageAsset } from '@/constants';
import { motion } from 'framer-motion';
import { FunctionComponent } from 'react';
/**************************************************************************************************************************
 TYPES / GLOBAL DEFINITIONS
***************************************************************************************************************************/
interface SectionHeaderProps {
  title: string;
  subtitle: string;
}

interface PageIntroProps {
  asset: ImageAsset;
  title: string;
  desc: String;
}
/**************************************************************************************************************************
 IMPLEMENTATIONS
***************************************************************************************************************************/
const SectionHeader: FunctionComponent<SectionHeaderProps> = ({
  title,
  subtitle
}: SectionHeaderProps) => {
  return (
    <motion.div
      className="w-fit min-h-[80px] lg:min-h-[80px] h-full bg-themeYellow-50/80 flex items-end justify-start gap-3 pr-5 shadow-md drop-shadow-sm"
      initial="initial"
      whileInView="entry"
      viewport={{ once: true }}
      variants={{
        initial: {
          y: -50,
          opacity: 0
        },
        entry: {
          y: 0,
          opacity: 1
        }
      }}
      transition={{
        duration: 0.75,
        delay: 0.5
      }}
    >
      <div className="w-fit h-full flex items-end justify-end">
        <div className="w-[6px] h-full bg-themeYellow-900"></div>
        <div className="w-[8px] h-full bg-themeBlue-900"></div>
        <div className="w-[10px] h-full bg-themePurple-900"></div>
      </div>
      <div className="h-full flex flex-col items-start justify-center py-2">
        <h4 className="text-md md:text-lg lg:text-xl text-themePurple-900 font-urbanist font-medium uppercase">
          {subtitle}
        </h4>
        <h3 className="text-2xl md:text-3xl lg:text-4xl text-themeDarkBlue-900 font-aldrich font-bold uppercase">
          {title}
        </h3>
      </div>
    </motion.div>
  );
};

const PageIntro: FunctionComponent<PageIntroProps> = ({ asset, title, desc }: PageIntroProps) => {
  return (
    <section className="relative w-full h-[40vh] lg:h-[55vh]">
      <img className="w-full h-full object-cover" src={asset.src} alt={asset.alt} />
      <div className="absolute top-0 left-0 w-full h-full bg-black/70"></div>
      <div className="absolute top-0 left-0 w-full h-full flex flex-col items-center justify-end gap-4 px-5 pb-10">
        <motion.h1
          className="text-4xl lg:text-5xl text-white font-poppins font-bold uppercase"
          initial="initial"
          whileInView="entry"
          viewport={{ once: true }}
          variants={{
            initial: {
              x: 100,
              opacity: 0
            },
            entry: {
              x: 0,
              opacity: 1
            }
          }}
          transition={{
            duration: 0.5
          }}
        >
          {title}
        </motion.h1>
        <motion.p
          className="text-md md:text-lg lg:text-xl text-themeYellow-200 text-center font-inter font-medium"
          initial="initial"
          whileInView="entry"
          viewport={{ once: true }}
          variants={{
            initial: {
              x: -100,
              opacity: 0
            },
            entry: {
              x: 0,
              opacity: 1
            }
          }}
          transition={{
            duration: 0.75,
            delay: 0.5
          }}
        >
          {desc}
        </motion.p>
      </div>
    </section>
  );
};

const BasicComponents = {
  SectionHeader,
  PageIntro
};

export default BasicComponents;
